import { useState, useEffect } from 'react';
import {
  getSubmissionPhotoUrls,
  getSubmissionLiveSiteUrl,
  getSubmissionListingMode,
  formatSubmissionStatusLabel,
  submissionStatusBadgeClass,
  normalizePipelineStatus,
  formatShareStructureSummary,
  submissionIsPublicOnSite,
} from '../../utils/submissions';
import { offeringFormFromRecord } from '../../utils/properties';
import { SUBMISSION_PUBLISH_STATUSES } from '../../config/constants';
import SubmissionPhotoGallery from '../submission/SubmissionPhotoGallery';
import PipelineProgress from '../submission/PipelineProgress';
import SubmissionPipelineControls from '../submission/SubmissionPipelineControls';
import AdminOfferingDetailsEditor from '../forms/AdminOfferingDetailsEditor';
import AdminRaiseStructureEditor from '../forms/AdminRaiseStructureEditor';
import AdminReviewLogPanel from '../forms/AdminReviewLogPanel';
import AdminSubmissionContentEditor from './AdminSubmissionContentEditor';

const AdminSubmissionDetail = ({ sub, onAdvance, onReject, onRequestApprovePin, pipelineBusy, onPublishToSite, publishBusy, onSaveRaisingStructure, onSaveOfferingDetails, onOfferingDraftChange, onSaveSubmissionContent, raisingSaveBusy, offeringSaveBusy, contentSaveBusy, addToast }) => {
  const submissionPhotos = getSubmissionPhotoUrls(sub);
  const liveSiteUrl = getSubmissionLiveSiteUrl(sub);
  const shareSummary = formatShareStructureSummary(sub);
  const [offeringForm, setOfferingForm] = useState(() => offeringFormFromRecord(sub));
  useEffect(() => {
    setOfferingForm(offeringFormFromRecord(sub));
  }, [
    sub?.id,
    sub?.fundingDeadline,
    sub?.projectedTimeline,
    sub?.riskRating,
    sub?.secOfferingType,
    sub?.propertyManager?.name,
    sub?.propertyManager?.licenseReference,
    sub?.trustee?.name,
    sub?.trustee?.regNumber,
    sub?.developer?.name,
    sub?.developer?.trackRecord
  ]);
  return (
  <div className="admin-submission admin-submission-detail">
    {submissionPhotos.length > 0 && (
      <div className="admin-submission-gallery-wrap">
        <SubmissionPhotoGallery photos={submissionPhotos} title={sub.title || "Listing"} />
      </div>
    )}
    <div className="admin-submission-body">
      <div className="agent-header">
        <div className="agent-company">
          <div className="agent-logo">{(sub.agentName || sub.title || "?").split(" ").map((n) => n[0]).join("").slice(0, 2)}</div>
          <div>
            <div className="submission-row-title-row" style={{ marginBottom: "0.35rem" }}>
              <h4 className="submission-row-title-text" style={{ margin: 0, fontSize: "1.05rem" }}>{sub.title}</h4>
              <span className={`status-badge listing-mode-badge status-${getSubmissionListingMode(sub) === "rent" ? "pending" : "approved"}`}>
                {getSubmissionListingMode(sub) === "rent" ? "For rent" : "For sale"}
              </span>
            </div>
            <p style={{ color: "var(--text-light)", fontSize: "0.85rem" }}>
              {sub.location} · {sub.agentCompany || "Independent"} · {sub.submitterEmail || sub.agentEmail}
            </p>
            <p style={{ color: "var(--text-light)", fontSize: "0.8rem", marginTop: "0.25rem" }}>
              Submitted {sub.submittedDate || "—"}
            </p>
          </div>
        </div>
        <span className={`status-badge status-${submissionStatusBadgeClass(sub.status)}`}>
          {formatSubmissionStatusLabel(sub.status)}
        </span>
      </div>
      <p className="pipeline-admin-hint">
        Use <strong>Send to …</strong> one step at a time. The listing goes on the public site only at <strong>Live on Site</strong>. Going back from Live removes it from the main page.
      </p>
      {SUBMISSION_PUBLISH_STATUSES.includes(normalizePipelineStatus(sub.status)) && (
        <div style={{ marginBottom: "1rem", padding: "0.75rem 1rem", background: "rgba(11, 31, 42, 0.06)", borderRadius: 10, border: "1px solid rgba(11, 31, 42, 0.15)" }}>
          {submissionIsPublicOnSite(sub) ? (
            <span style={{ fontSize: "0.9rem", color: "var(--primary)", fontWeight: 600 }}>
              <i className="fas fa-globe"></i> Published on main Properties page
            </span>
          ) : sub.publishedPropertyId ? (
            <span style={{ fontSize: "0.9rem", color: "var(--warning)", fontWeight: 600 }}>
              <i className="fas fa-eye-slash"></i> Removed from public site — not visible to investors
            </span>
          ) : (
            <>
              <p style={{ margin: "0 0 0.5rem", fontSize: "0.9rem", color: "var(--text-light)" }}>
                Not on the public site yet. Publish now if approval did not sync.
              </p>
              <button type="button" className="btn btn-primary btn-sm" disabled={publishBusy} onClick={() => onPublishToSite(sub.id)}>
                {publishBusy ? "Publishing…" : "Publish to main page"}
              </button>
            </>
          )}
        </div>
      )}
      <div className="admin-pipeline-block">
        <PipelineProgress currentStatus={sub.status || "listed"} liveListingUrl={liveSiteUrl} />
        <div className="admin-pipeline-controls">
          <SubmissionPipelineControls
            sub={sub}
            onAdvance={onAdvance}
            onReject={onReject}
            onRequestApprovePin={onRequestApprovePin}
            pipelineBusy={pipelineBusy}
            addToast={addToast}
          />
        </div>
        <AdminReviewLogPanel
          sub={sub}
          hint="Shown on the submission review queue (Admin → Submissions → select a listing)."
          collapsible
        />
      </div>
      <div style={{ display: "flex", gap: "2rem", fontSize: "0.9rem", color: "var(--text-light)", flexWrap: "wrap", marginBottom: "1rem" }}>
        <span><i className="fas fa-tag" style={{ color: "var(--primary)" }}></i> {sub.type}</span>
        <span><i className="fas fa-dollar-sign" style={{ color: "var(--primary)" }}></i> ${Number(sub.price || 0).toLocaleString()}</span>
        <span><i className="fas fa-user" style={{ color: "var(--primary)" }}></i> {sub.agentName}</span>
        {shareSummary && (
          <span><i className="fas fa-chart-pie" style={{ color: "var(--primary)" }}></i> {shareSummary}</span>
        )}
        {submissionPhotos.length > 0 && (
          <span><i className="fas fa-images" style={{ color: "var(--primary)" }}></i> {submissionPhotos.length} photos</span>
        )}
      </div>
      <AdminSubmissionContentEditor
        sub={sub}
        saving={contentSaveBusy}
        addToast={addToast}
        onSave={(body) => onSaveSubmissionContent(sub.id, body)}
      />
      <AdminOfferingDetailsEditor
        record={sub}
        form={offeringForm}
        setForm={setOfferingForm}
        onDraftChange={(body) => onOfferingDraftChange(sub.id, body)}
        onSave={(body) => onSaveOfferingDetails(sub.id, body)}
        saving={offeringSaveBusy}
        hint="Set before Live on Site so investors see property manager, risk, and timeline on day one. You can also edit later under Live Listings → Manage."
      />
      <AdminRaiseStructureEditor sub={sub} onSave={onSaveRaisingStructure} saving={raisingSaveBusy} addToast={addToast} />
      {sub.notes && <p style={{ fontSize: "0.85rem", color: "var(--text-light)", marginBottom: "1rem" }}>{sub.notes}</p>}
    </div>
  </div>
  );
};

export default AdminSubmissionDetail;
